/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, isEmpty, Modal, ModalBody, ModalFooter, ModalFooterButton, ModalSidebar, Text, useTheme } from 'bold-ui'
import { Form, FormRenderProps, resolveName } from 'components/form'
import { IconLabel } from 'components/IconLabel'
import { confirm } from 'components/modals/confirm'
import createDecorator from 'final-form-calculate'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { MutableRefObject, useRef, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { metaPath } from 'util/metaPath'

import { consultaSeguimentoCalculator } from '../calculator-consultaSeguimento'
import {
  CONSULTA_SEGUIMENTO_URL_MODAL,
  ConsultaSeguimentoFieldModel,
  ConsultaSeguimentoModalTab,
  ConsultaSeguimentoPerguntaStatusEnum,
  ConsultaSeguimentoSelectionMode,
  ConsultaSeguimentoSelectionState,
} from '../model-consultaSeguimento'
import {
  getAplicacaoWithLastQuestionAsNextStatus,
  getAplicacaoWithLastQuestionAsPrevStatus,
  getPerguntaAtiva,
  getPerguntaNextStatus,
  getPerguntasSelecionadasInitialStatus,
} from '../utils-consultaSeguimento'
import { validatorConsultaSeguimento } from '../validator-consultaSeguimento'
import { ConsultaSeguimentoModalFooterCounter } from './ConsultaSeguimentoModalFooterCounter'
import { ConsultaSeguimentoModalStepper } from './ConsultaSeguimentoModalStepper'
import { ConsultaSeguimentoModalTabAdicionar } from './ConsultaSeguimentoModalTabAdicionar'
import { ConsultaSeguimentoModalTabResultado } from './ConsultaSeguimentoModalTabResultado'
import { ConsultaSeguimentoModalTabSelecao } from './ConsultaSeguimentoModalTabSelecao'

interface ConsultaSeguimentoModalProps {
  currentValue: ConsultaSeguimentoFieldModel
  perguntasFalhasNoMChat: MChatPerguntaEnum[]
  aplicarButtonRef: React.RefObject<HTMLButtonElement>
  onSave: (values: ConsultaSeguimentoFieldModel) => void
}

const path = metaPath<ConsultaSeguimentoFieldModel>()
const decorator = createDecorator(...consultaSeguimentoCalculator)

export function ConsultaSeguimentoModal(props: Readonly<ConsultaSeguimentoModalProps>) {
  const { currentValue, perguntasFalhasNoMChat, aplicarButtonRef, onSave } = props

  const theme = useTheme()
  const history = useHistory()
  const match = useRouteMatch()

  const hasValues = !isEmpty(currentValue)

  const backToSelecaoButtonRef = useRef<HTMLButtonElement>()
  const backToAplicacaoButtonRef = useRef<HTMLButtonElement>()
  const backQuestionButtonRef = useRef<HTMLButtonElement>()
  const modalContentRef = useRef<HTMLDivElement>()
  const numberOfPerguntasFalhasNoMChat = perguntasFalhasNoMChat?.length
  const hasPerguntasFalhasNoMChat = numberOfPerguntasFalhasNoMChat > 0
  const isMChatRiscoBaixo = hasPerguntasFalhasNoMChat && numberOfPerguntasFalhasNoMChat < 3

  const [currentTab, setCurrentTab] = useState<ConsultaSeguimentoModalTab>(
    hasValues ? ConsultaSeguimentoModalTab.RESULTADO : ConsultaSeguimentoModalTab.SELECAO
  )
  const [selectionState, setSelectionState] = useState<ConsultaSeguimentoSelectionState>({ failed: false, values: [] })

  const initialValues: MutableRefObject<ConsultaSeguimentoFieldModel> = useRef({
    perguntasSelecionadas: perguntasFalhasNoMChat,
    aplicacao: null,
    selectionMode:
      hasPerguntasFalhasNoMChat && !isMChatRiscoBaixo
        ? ConsultaSeguimentoSelectionMode.AUTOMATICO
        : ConsultaSeguimentoSelectionMode.MANUAL,
  })

  const handleStart = ({ values, form }: FormRenderProps<ConsultaSeguimentoFieldModel>) => {
    const isSelectionModeAutomatico = values.selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO
    const isSelectionModeManual = values.selectionMode === ConsultaSeguimentoSelectionMode.MANUAL
    const sortedPerguntasSelecionadas = [...values.perguntasSelecionadas].sort((a, b) => a.localeCompare(b))

    const perguntasSelecionadasBySelectionMode = isSelectionModeAutomatico
      ? perguntasFalhasNoMChat
      : sortedPerguntasSelecionadas

    const isInvalid = isSelectionModeManual && !form.getFieldState(resolveName(path.perguntasSelecionadas)).valid

    if (isInvalid) {
      setSelectionState({
        failed: true,
        values: values.perguntasSelecionadas,
      })
      modalContentRef.current.scrollTo({ behavior: 'smooth', top: 0 })
      return null
    }

    form.change('', {
      selectionMode: values.selectionMode,
      perguntasSelecionadas: perguntasSelecionadasBySelectionMode,
      aplicacao: getPerguntasSelecionadasInitialStatus(perguntasSelecionadasBySelectionMode),
    })

    setCurrentTab(ConsultaSeguimentoModalTab.APLICACAO)
    setTimeout(() => backToSelecaoButtonRef.current?.focus())
  }

  const handleNext = ({ values, form }: FormRenderProps<ConsultaSeguimentoFieldModel>) => {
    const { perguntaAtiva, indexOfPerguntaAtiva, isLastPergunta } = getPerguntaAtiva(values.aplicacao)

    const isInvalid = !form.getFieldState(resolveName(path.aplicacao[perguntaAtiva])).valid
    if (isInvalid) return null

    if (!isLastPergunta) {
      const valuesOfPerguntaAtiva = values.aplicacao[perguntaAtiva]

      form.change(resolveName(path.aplicacao[perguntaAtiva]), {
        ...valuesOfPerguntaAtiva,
        status: getPerguntaNextStatus(valuesOfPerguntaAtiva.status),
      })

      const nextPergunta = values.perguntasSelecionadas[indexOfPerguntaAtiva + 1]

      form.change(resolveName(path.aplicacao[nextPergunta]), {
        ...values.aplicacao[nextPergunta],
        status: ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
      })

      setTimeout(() => backQuestionButtonRef.current.focus())
    }

    if (isLastPergunta) {
      onSave({ ...values, aplicacao: getAplicacaoWithLastQuestionAsNextStatus(values.aplicacao) })
      setCurrentTab(ConsultaSeguimentoModalTab.RESULTADO)
      setTimeout(() => backToAplicacaoButtonRef.current.focus())
    }
  }

  const handleCloseModal = () => {
    const goBack = () => {
      history.push(match.url.split(CONSULTA_SEGUIMENTO_URL_MODAL)[0])
      setTimeout(() => aplicarButtonRef?.current?.focus())
    }

    if (currentTab === ConsultaSeguimentoModalTab.APLICACAO) {
      closeModalConfirmDialog(goBack)
    } else {
      goBack()
    }
  }

  const handleGoBackToAplicacao = (form: FormRenderProps<ConsultaSeguimentoFieldModel>['form']) => {
    form.reset({ ...currentValue, aplicacao: getAplicacaoWithLastQuestionAsPrevStatus(currentValue.aplicacao) })
    setCurrentTab(ConsultaSeguimentoModalTab.APLICACAO)
  }

  const handleGoBackToSelecao = () => {
    setCurrentTab(ConsultaSeguimentoModalTab.SELECAO)
  }

  const clearStartFailed = () => {
    setSelectionState((prevState) => ({
      failed: false,
      values: prevState.values,
    }))
  }

  const renderModal = (renderProps: FormRenderProps<ConsultaSeguimentoFieldModel>) => {
    const { perguntaAtiva, isLastPergunta } = getPerguntaAtiva(renderProps.values.aplicacao)
    const errors = renderProps.errors ?? {}
    const isInvalid = perguntaAtiva in errors.aplicacao

    return (
      <Modal open closeOnBackdropClick={false} onClose={handleCloseModal} style={styles.modal}>
        <ModalSidebar position='left' style={styles.sidebar}>
          <ConsultaSeguimentoModalStepper aplicacao={renderProps.values.aplicacao} status={currentTab} />
        </ModalSidebar>
        <ModalBody>
          <div ref={modalContentRef}>
            {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
              <ConsultaSeguimentoModalTabSelecao
                path={path}
                perguntasFalhasNoMChat={perguntasFalhasNoMChat}
                selectionState={selectionState}
                clearStartFailed={clearStartFailed}
              />
            )}
            {currentTab === ConsultaSeguimentoModalTab.APLICACAO && (
              <ConsultaSeguimentoModalTabAdicionar
                path={path.aplicacao}
                values={renderProps.values}
                backToSelecaoButtonRef={backToSelecaoButtonRef}
                backQuestionButtonRef={backQuestionButtonRef}
                onGoBackToSelecao={handleGoBackToSelecao}
              />
            )}
            {currentTab === ConsultaSeguimentoModalTab.RESULTADO && (
              <ConsultaSeguimentoModalTabResultado
                value={currentValue}
                backToAplicacaoButtonRef={backToAplicacaoButtonRef}
                onGoBackToAplicacao={() => handleGoBackToAplicacao(renderProps.form)}
              />
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <HFlow alignItems='center' justifyContent='space-between'>
            <Text color='secondary' style={styles.authorsLabel}>
              © 2009 Robins, Fein, & Barton
            </Text>

            <HFlow justifyContent='flex-end' alignItems='center'>
              {currentTab === ConsultaSeguimentoModalTab.RESULTADO && (
                <IconLabel
                  icon='checkCircleOutline'
                  text='Resultado salvo no atendimento'
                  color={theme.pallete.status.success.c40}
                />
              )}

              {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
                <ConsultaSeguimentoModalFooterCounter
                  name={path.perguntasSelecionadas}
                  selectionMode={renderProps.values.selectionMode}
                  isMChatRiscoBaixo={isMChatRiscoBaixo}
                  numberOfPerguntasFalhasNoMChat={numberOfPerguntasFalhasNoMChat}
                  startFailed={selectionState.failed}
                />
              )}

              <ModalFooterButton onClick={handleCloseModal}>
                {currentTab === ConsultaSeguimentoModalTab.RESULTADO ? 'Fechar' : 'Cancelar'}
              </ModalFooterButton>

              {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
                <ModalFooterButton kind='primary' onClick={() => handleStart(renderProps)}>
                  Iniciar
                </ModalFooterButton>
              )}

              {currentTab === ConsultaSeguimentoModalTab.APLICACAO && (
                <ModalFooterButton kind='primary' onClick={() => handleNext(renderProps)} disabled={isInvalid}>
                  {isLastPergunta ? 'Calcular resultado' : 'Continuar'}
                </ModalFooterButton>
              )}
            </HFlow>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<ConsultaSeguimentoFieldModel>
      initialValues={initialValues.current}
      render={renderModal}
      validate={validatorConsultaSeguimento}
      decorators={[decorator]}
    />
  )
}

const closeModalConfirmDialog = (handleConfirm: () => void) => {
  confirm({
    title: 'Deseja sair sem finalizar a Entrevista de Seguimento?',
    icon: 'exclamationTriangleOutline',
    confirmLabel: 'Sair da tela',
    cancelLabel: 'Continuar aqui',
    type: 'primary',
    size: 'small',
    onConfirm: handleConfirm,
    render: () => <Text>As alterações realizadas serão perdidas, inclusive resultados já calculados.</Text>,
  })()
}

const styles = {
  modal: css`
    display: grid;
    grid-template-rows: 1fr auto;
    width: 62rem;
    max-width: 62rem;
    overflow: hidden;
    height: 80vh;
  `,
  sidebar: css`
    padding: 2rem 1.5rem 1.5rem 2rem;
  `,
  authorsLabel: css`
    padding-left: 1rem;
  `,
}
